export interface PortalThemeColors {
  portalPrimaryColor?: string;
  portalSecondaryColor?: string;
  portalHeaderColor?: string;
  portalFooterColor?: string;
  portalFeatureCardColor?: string;
  portalLogoBackgroundColor?: string;
}

const LIGHT_TEXT = '#FFFFFF';
const DARK_TEXT = '#1A1A1A';

export function readableTextColor(background: string): string | null {
  const hex = (background || '').trim().replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }

  const channel = (value: number): number => {
    const sRgb = value / 255;
    return sRgb <= 0.03928
      ? sRgb / 12.92
      : Math.pow((sRgb + 0.055) / 1.055, 2.4);
  };

  const luminance =
    0.2126 * channel(parseInt(hex.slice(0, 2), 16)) +
    0.7152 * channel(parseInt(hex.slice(2, 4), 16)) +
    0.0722 * channel(parseInt(hex.slice(4, 6), 16));

  return luminance > 0.45 ? DARK_TEXT : LIGHT_TEXT;
}

export function applyPortalTheme(
  root: HTMLElement,
  config: PortalThemeColors | null | undefined,
): void {
  if (!root || !config) {
    return;
  }

  if (config.portalPrimaryColor) {
    root.style.setProperty('--tg-theme-secondary', config.portalPrimaryColor);
  }
  if (config.portalSecondaryColor) {
    root.style.setProperty('--tg-theme-primary', config.portalSecondaryColor);
  }

  const regions: { value?: string; background: string; foreground?: string }[] =
    [
      {
        value: config.portalHeaderColor,
        background: '--tg-portal-header-bg',
        foreground: '--tg-portal-header-fg',
      },
      {
        value: config.portalFooterColor,
        background: '--tg-portal-footer-bg',
        foreground: '--tg-portal-footer-fg',
      },
      {
        value: config.portalFeatureCardColor,
        background: '--tg-portal-feature-card-bg',
        foreground: '--tg-portal-feature-card-fg',
      },
      {
        value: config.portalLogoBackgroundColor,
        background: '--tg-portal-logo-bg',
      },
    ];

  regions.forEach((region) => {
    if (!region.value) {
      root.style.removeProperty(region.background);
      if (region.foreground) {
        root.style.removeProperty(region.foreground);
      }
      return;
    }
    root.style.setProperty(region.background, region.value);
    if (!region.foreground) {
      return;
    }
    const textColor = readableTextColor(region.value);
    if (textColor) {
      root.style.setProperty(region.foreground, textColor);
    }
  });
}
